import { ReactNode, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { DropdownItem } from 'reactstrap';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import i18n from 'src/utils/translate';
import ButtonDropdown from 'src/components/buttons/buttonDropdown';
import { text } from 'styles-js/mixins/typography';

export type Item = {
  as?: string;
  content: string | ReactNode;
  id: string;
  link: string;
};
type Props = {
  isCompact?: boolean;
  items: Item[];
  selectedPage?: string;
  onClicked?: ({ as, content, id, link }: Item, e?: SyntheticEvent) => void;
}

let visibleItemsNumber = 0;

export default function NavBar({ items, isCompact, onClicked, selectedPage }: Props) {
  visibleItemsNumber = !isCompact ? 3 : 5;

  const itemInMoreDropdownFromIndex = items.length > visibleItemsNumber && isCompact
    ? visibleItemsNumber - 1
    : visibleItemsNumber;

  return (
    <HeaderNavBar>
      <StyledNavBar data-testid="nav-bar">
        {items.map((item, i) => (
          <NavBarItem
            index={i}
            isCompact={isCompact}
            item={item}
            key={item.id}
            selectedPage={selectedPage}
            totalItems={items.length}
            onClicked={onClicked}
          />
        ))}
        {items.length > visibleItemsNumber && (
          <More $isCompact={isCompact} className="visible-xxs" data-testid="navbar-more">
            <ButtonDropdown color="none" isRightAligned={true} text={i18n.t('More')}>
              {items.slice(itemInMoreDropdownFromIndex).map(item => (
                <DropdownItem key={item.id} onClick={() => window.location.href = item.as || item.link}>
                  {item.content}
                </DropdownItem>
              ))}
            </ButtonDropdown>
          </More>
        )}
      </StyledNavBar>
    </HeaderNavBar>
  );
}

function NavBarItem({ index, isCompact, item, selectedPage, totalItems, onClicked }) {
  const itemClassName = classNames({ 'hidden-xxs': isCompact
    ? totalItems > 5 && index >= (visibleItemsNumber - 1)
    : totalItems >= 5 && index >= visibleItemsNumber,
  });
  if (item.as) {
    return (
      <li>
        <Link
          legacyBehavior
          passHref
          as={item.as}
          href={item.link}
        >
          <NavBarLink
            $isActive={selectedPage === item.id}
            $isCompact={isCompact}
            className={itemClassName}
            data-testid="nav-bar-item"
            onClick={(e) => {
              if (onClicked) onClicked(item, e);
            }}
          >
            {item.content}
          </NavBarLink>
        </Link>
      </li>
    );
  } else {
    return (
      <li>
        <NavBarLink
          $isActive={selectedPage === item.id}
          $isCompact={isCompact}
          className={itemClassName}
          href={item.link}
          onClick={(e) => {
            if (onClicked) onClicked(item, e);
          }}
        >
          {item.content}
        </NavBarLink>
      </li>
    );
  }
}

const HeaderNavBar = styled.div`
  display: flex;
  border-bottom: ${({ theme }) => theme.greyLine};
`;

const StyledNavBar = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavBarLink = styled.a<{ $isActive?: boolean; $isCompact?: boolean }>`
  ${text};
  display: block;
  height: 60px;
  padding: 0 20px;
  line-height: 55px;
  border-bottom: 5px solid transparent;
  &:hover {
    color: ${props => props.theme.colorTextBlack};
    border-bottom-color:  ${props => props.theme.colorGreen};
  }
  @media (${({ theme }) => theme.underScreenSmall}) {
    height: ${({ theme }) => theme.navHeightTop};
    padding: 0 8px;
    line-height: 45px;
  }

  ${({ $isCompact }) => $isCompact && css`
    @media (${({ theme }) => theme.underScreenSmall}) {
      font-size: ${props => props.theme.fontSizeXs};
    }
    @media (${({ theme }) => theme.underScreenXsmall}) {
      padding: 0 5px; // for community header
    }
  `}

  ${({ $isActive }) => $isActive && css`
    border-bottom-color:  ${props => props.theme.colorGreen};
  `}
`;

const More = styled.li<{ $isCompact?: boolean }>`
  .btn-group {
    display: block;
    z-index: 100;
  }
  // Similar styles than NavBarLink above
  .dropdown-toggle {
    display: block;
    height: 60px;
    margin: 0;
    padding: 0 20px;
    line-height: 55px;
    border: 0;
    border-bottom: 5px solid transparent;
    @media (${({ theme }) => theme.underScreenSmall}) {
      height: ${({ theme }) => theme.navHeightTop};
      padding: 0 8px;
      line-height: 45px;
    }
  }

  ${({ $isCompact }) => $isCompact && css`
    .dropdown-toggle {
      @media (${({ theme }) => theme.underScreenSmall}) {
        font-size: ${props => props.theme.fontSizeXs};
      }
      @media (${({ theme }) => theme.underScreenXsmall}) {
        padding: 0 5px; // for community header
      }
    }
  `}

  .dropdown-item {
    line-height: ${props => props.theme.fontLineHeightS};
  }
`;
